import React from 'react';
import Grid from '@mui/material/Grid';
import {
    Form,
    Field,
    MeetingSetup,
    FormType,
    InputEventProps,
    GridDivider,
    ActionButton,
    FormCheckbox,
    Alert
} from '..';
import { useAlert, useFetch } from '../../hooks';

type amdinsTypes = 'Christo' | 'Gerhard' | 'Dané' | 'Sunnette';
const adminOptions: Array<amdinsTypes> = [
    'Christo',
    'Gerhard',
    'Dané',
    'Sunnette'
];

interface bookProps {
    Name: string;
    Email: string;
    Phone: string;
    Note: string;
    Christo: boolean;
    Gerhard: boolean;
    Dané: boolean;
    Sunnette: boolean;
}

const bookInitialState: bookProps = {
    Name: '',
    Email: '',
    Phone: '',
    Note: '',
    Christo: false,
    Gerhard: false,
    Dané: false,
    Sunnette: false
};

export const BookForm: React.FC = React.memo(() => {
    const [online, setOnline] = React.useState(true);
    const [fetchHandler, loading] = useFetch('/dbAddBooking', 'POST');
    const [book, setBook] = React.useState<bookProps>(bookInitialState);
    const { alert, dispatchAlert, closeAlert } = useAlert();

    const onChangeHandler = React.useCallback(
        (key: keyof bookProps, variant?: 'checkbox') => (e: InputEventProps) =>
            setBook(prevBook => {
                return {
                    ...prevBook,
                    [key]:
                        variant === 'checkbox'
                            ? e.target.checked
                            : e.target.value
                };
            }),
        []
    );

    const errorHandler = React.useCallback(
        message => {
            dispatchAlert({
                type: 'UPDATE_ALERT',
                message: message,
                severity: 'error'
            });
        },
        [dispatchAlert]
    );

    const successHandler = React.useCallback(() => {
        dispatchAlert({
            type: 'UPDATE_ALERT',
            message: 'Success!',
            severity: 'success'
        });
    }, [dispatchAlert]);

    const bookHandler = React.useCallback(
        async (e: FormType) => {
            e.preventDefault();
            const result = await fetchHandler({ data: book });
            result.success ? successHandler() : errorHandler(result.msg);
            dispatchAlert({ type: 'OPEN' });
        },
        [fetchHandler, book, errorHandler, successHandler, dispatchAlert]
    );

    return (
        <Form onSubmit={bookHandler} className='contact--book__form'>
            <Alert closeHandler={closeAlert} {...alert} />
            <MeetingSetup />
            <GridDivider />
            <Grid item xs={12}>
                <h3 style={{ fontWeight: 500, color: '#555' }}>
                    Your details:
                </h3>
            </Grid>
            <Field name='Name' onChange={onChangeHandler('Name')} />
            <Field name='Email' sm={6} onChange={onChangeHandler('Email')} />
            <Field
                name='Phone'
                sm={6}
                onChange={onChangeHandler('Phone')}
                optional
            />
            <Field
                name='Note'
                label='Notes'
                rows={3}
                onChange={onChangeHandler('Note')}
                multiline
            />
            <GridDivider />
            <Grid item xs={12}>
                <h3 style={{ fontWeight: 500, color: '#555' }}>
                    Meet Face to Face or Online:
                </h3>
            </Grid>
            <ActionButton
                title='Online'
                type='button'
                sm={6}
                sx={{
                    pt: '1.5rem',
                    pb: '1.5rem'
                }}
                color={online ? 'secondary' : 'inherit'}
                variant={online ? 'contained' : 'outlined'}
                onClick={() => setOnline(true)}
                disableElevation
                disableFocusRipple
                disableTouchRipple
                grid
            />
            <ActionButton
                title='Face to Face'
                type='button'
                sm={6}
                sx={{
                    pt: '1.5rem',
                    pb: '1.5rem'
                }}
                color={online ? 'inherit' : 'secondary'}
                variant={online ? 'outlined' : 'contained'}
                onClick={() => setOnline(false)}
                disableElevation
                disableFocusRipple
                disableTouchRipple
                grid
            />
            <GridDivider />
            <Grid item xs={12}>
                <h3 style={{ fontWeight: 500, color: '#555' }}>
                    Who would you like to meet with:
                </h3>
            </Grid>
            {adminOptions.map(adminOption => (
                <FormCheckbox
                    sm={3}
                    name={adminOption}
                    label={adminOption}
                    onChange={onChangeHandler(adminOption, 'checkbox')}
                    optional
                />
            ))}
            <GridDivider />
            <ActionButton
                title='Book'
                type='submit'
                size='large'
                variant='contained'
                color='primary'
                loading={loading}
                grid
            />
        </Form>
    );
});
